import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, Col, Container, Row, Spinner, Stack } from "react-bootstrap";
import { currentUrl, unsavedChanges } from "../../../App";
import { fetchBeds, fetchRooms, postBed, postRoom } from "../../../services/httpClient";
import PageTitle from "../../ui/PageTitle";
import useApp from "../../../utils/useApp";
import RoomManagementCard from "./RoomManagementCard";
import BedManagement from "./BedManagement";
import { bedsOfRoom } from "../../../helpers/housingAssignment";

interface BedManagementState {
  getData: boolean;
  setOrgToSave: any;
  setFacilityUpdated: any;
  setRoomsUpdated: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const OrgBedManagement = ({
  getData = false,
  setOrgToSave,
  setFacilityUpdated,
  setRoomsUpdated,
  setShowToast,
  setToastText,
  setToastBody,
}: BedManagementState) => {
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { auth }: any = useApp();
  const [errMsg, setErrMsg] = useState("");
  const [rooms, setRooms] = useState<any[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (auth?.profile.includeHMIS === 1) {
      getAll();
      setRoomsUpdated(false);
    }
  }, [auth?.profile]);

  useEffect(() => {
    if (getData) {
      getAll();
      setRoomsUpdated(false);
    }
  }, [getData]);

  const getAll = () => {
    fetchBeds()
      .then((res: any) => {
        const bedList = res[0].data;
        return fetchRooms().then((roomRes: any) => {
          let roomList = roomRes[0].data;
          // standalone rooms only - rooms belonging to a dwelling or facility are managed from those screens
          roomList = roomList.filter((room: any) => !room.property_id);
          roomList = roomList.map((room: any) => ({
            ...room,
            bed_count: `${bedsOfRoom(bedList, room.id).length} beds`,
            beds: bedsOfRoom(bedList, room.id),
          }));
          roomList.sort((a: any, b: any) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
          setRooms(roomList);
          // keep the drilled-in room's bed list current after any add/edit/delete
          setSelectedRoom((current: any) => (current ? roomList.find((r: any) => r.id === current.id) || null : null));
        });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
        setErrMsg("");
      });
  };

  const getNextRoomName = (existingRooms: any[]) => {
    const existingNames = new Set(existingRooms.map((r: any) => r.name));
    let n = existingRooms.length + 1;
    while (existingNames.has(`Room ${n}`)) {
      n++;
    }
    return `Room ${n}`;
  };

  const onClickAddRoom = () => {
    const roomName = getNextRoomName(rooms);
    postRoom({ name: roomName })
      .then(() => {
        setShowToast(true);
        setToastText("Room Created");
        setToastBody(roomName);
        setFacilityUpdated(true);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getAll();
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  const onClickAddBed = () => {
    setErrMsg("");
    const roomId = selectedRoom?.id;
    const bedName = selectedRoom?.beds?.length ? `Bed ${selectedRoom?.beds?.length + 1}` : "Bed 1";
    postBed({ room_id: roomId, name: bedName })
      .then(() => {
        setShowToast(true);
        setToastText("Bed Created");
        setToastBody(bedName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getAll();
      })
      .catch((err: any) => setErrMsg(err?.response?.data?.msg));
  };

  return (
    <>
      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
            {errMsg}
          </p>
          <PageTitle
            title={selectedRoom ? `${selectedRoom.name}` : "Rooms"}
            type="section"
            subTitleText={!selectedRoom ? "Standalone rooms only - rooms that aren't part of a dwelling or facility." : undefined}
            buttonTitle={selectedRoom ? "Add Bed" : "Add Room"}
            buttonColor={"primary"}
            onButtonClick={selectedRoom ? () => onClickAddBed() : () => onClickAddRoom()}
            backButtonText={"Back to Rooms"}
            onBackClick={selectedRoom ? () => setSelectedRoom(null) : null}
          />
          {selectedRoom ? (
            <BedManagement
              getItems={getAll}
              beds={selectedRoom?.beds || []}
              setOrgToSave={setOrgToSave}
              setErrMessage={setErrMsg}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          ) : (
            <Row className="mt-3">
              <Col xs={12} lg={10}>
                {rooms.length === 0 ? (
                  <p className="mt-2">No Rooms Created</p>
                ) : (
                  <Stack gap={2}>
                    {rooms?.map((room: any, index: number) => (
                      <Row key={index}>
                        <Col xs={12}>
                          <RoomManagementCard
                            room={room}
                            getItems={getAll}
                            setSelectedRoom={setSelectedRoom}
                            setOrgToSave={setOrgToSave}
                            setErrMessage={setErrMsg}
                            setShowToast={setShowToast}
                            setToastText={setToastText}
                            setToastBody={setToastBody}
                          />
                        </Col>
                      </Row>
                    ))}
                  </Stack>
                )}
              </Col>
            </Row>
          )}

          <Row className="mb-5"></Row>
        </>
      )}
    </>
  );
};

export default React.memo(OrgBedManagement);
